import Button from 'components/UI/CoreUIButton'
import Spin from 'components/UI/Spin'
import { API_URL_TRAINING_WISH_DETAILS, API_URL_TRAINING_WISH_PROVISION, API_URL_TRAINING_WISHES } from 'constants/api'
import { useI18n } from 'context/I18nProvider'
import { useNotification } from 'context/NotificationProvider'
import { useQuery } from 'context/QueryProvider'
import useSWR from 'hooks/swr'
import { useTableState } from 'hooks/useTableState'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Distribution, { type TrainingWishProvision } from './Distribution'
import Header from './Header'
import SubWishes from './SubWishes'
import UserSelect from './UserSelect'

interface TrainingWishChild {
  username: string
}

function AssignProvision() {
  const { i18n } = useI18n()
  const navigate = useNavigate()
  const { id = '' } = useParams()
  const { notifyError, notifySuccess } = useNotification()
  const { client } = useQuery()

  const { current, pageSize, handleChange, total, setTotalOfItems } = useTableState({ defaultPageSize: 20 })
  const pagination = {
    current,
    pageSize,
    handleChange,
    total,
  }

  const {
    data: trainingWish,
    isValidating: isWishValidating,
    mutate: mutateWish,
  } = useSWR(API_URL_TRAINING_WISH_DETAILS.replace('ID', id)) as {
    data?: TrainingWishProvision
    isValidating: boolean
    mutate: () => Promise<unknown>
  }
  const {
    data: wishes,
    isValidating: isChildrenValidating,
    mutate: mutateChildren,
  } = useSWR(`${API_URL_TRAINING_WISHES}?provision=${id}&page=${current}&nbpp=${pageSize}`, {
    onSuccess: resp => setTotalOfItems(resp.nbResults),
  }) as {
    data?: { results?: TrainingWishChild[]; nbResults: number }
    isValidating: boolean
    mutate: () => Promise<unknown>
  }
  const isValidating = isWishValidating || isChildrenValidating
  const mutate = () => mutateWish().then(mutateChildren)

  const [users, setUsers] = useState<string[]>([])
  const nbUsers = users ? users.length : 0

  const submit = async () => {
    try {
      await client.post(`${API_URL_TRAINING_WISH_PROVISION.replace('ID', id)}`, {
        users,
      })
      setUsers([])
      notifySuccess()
      await mutate()
    } catch (e) {
      notifyError(e)
    }
  }

  const changeInitial = async (initial: number) => {
    if (!trainingWish) return

    try {
      await client.patch(`${API_URL_TRAINING_WISHES}/${trainingWish.id}`, {
        initialProvisions: initial,
      })
      notifySuccess()
      await mutate()
    } catch (e) {
      notifyError(e)
    }
  }

  if (isValidating || !trainingWish || !wishes) {
    return <Spin size="middle" />
  }

  return (
    <div className="flex flex-col gap-4">
      <Button variant="tertiary" onClick={() => navigate(-1)} leftIcon="arrow-left">
        {i18n('label.back')}
      </Button>
      <Header trainingWish={trainingWish} onUpdate={mutate} />
      <Distribution trainingWish={trainingWish} nbUsers={nbUsers} onInitialChange={changeInitial} />
      <SubWishes
        wishes={{ ...wishes, results: wishes.results ?? [] }}
        pagination={{ ...pagination, handleChange: handleChange as () => void }}
      />
      <div className="w-full flex justify-between my-8">
        <UserSelect
          trainingWish={trainingWish}
          onChange={setUsers}
          value={users}
          onSubmit={submit}
          selected={wishes.results?.map(u => u.username)}
        />
      </div>
    </div>
  )
}

export default AssignProvision
